import {EtsyList, EtsyListing, EtsyListingStateEnum, EtsyParamIncludesEnum} from '@coolcolduk/etsy';
import {InfiniteScroll, useMessage} from '@coolcolduk/ui-mui';
import {uniqueObjectWithKey} from '@coolcolduk/util';
import {CircularProgress, Grid} from '@mui/material';
import {useEffect, useState} from 'react';
import EtsyListingStateSelector from '../../../components/etsy/EtsyListingStateSelector';
import {fetchListing} from '../../../hook/useGetListing';
import {fetchListingsByShop} from '../../../hook/useGetListingsByShop';
import {firebaseAuth} from '../../../setup/firebase';
import {FeatureProps} from '../../../types/FeatureObject';
import EtsyUpdateListingSeoItem from './EtsyUpdateListingSeoItem';

export interface EtsyUpdateListingSeoProps extends FeatureProps {}

const emptyEtsyList: EtsyList<EtsyListing> = {count: 0, results: []};
const emptyEtsyListRecord: Record<EtsyListingStateEnum, EtsyList<EtsyListing>> = {
  [EtsyListingStateEnum.ACTIVE]: emptyEtsyList,
  [EtsyListingStateEnum.INACTIVE]: emptyEtsyList,
  [EtsyListingStateEnum.SOLD_OUT]: emptyEtsyList,
  [EtsyListingStateEnum.DRAFT]: emptyEtsyList,
  [EtsyListingStateEnum.EXPIRED]: emptyEtsyList,
};
export default function EtsyUpdateListingSeo({selectedAccount, user}: EtsyUpdateListingSeoProps) {
  const [state, setState] = useState<EtsyListingStateEnum | undefined>(undefined);
  const [listings, setListings] = useState<Record<EtsyListingStateEnum, EtsyList<EtsyListing>>>(emptyEtsyListRecord);
  const {showMessage} = useMessage();

  // auth or state change
  useEffect(() => {
    firebaseAuth.currentUser?.getIdToken().then((token) => {
      if (!token) {
        setListings(emptyEtsyListRecord);
        return;
      }

      // fetch listings if state is set and there are no listings
      if (state && listings[state].results.length === 0)
        fetchListingsByShop(token, selectedAccount.id, {
          state,
          includes: [EtsyParamIncludesEnum.IMAGES],
          limit: 10,
        }).then((response) => {
          setListings((prev) => ({...prev, [state]: response}));
        });
    });
  }, [state, selectedAccount.id, listings]);

  const data = state ? listings[state]?.results || [] : [];
  return (
    <Grid container direction="column" spacing={2}>
      <Grid>
        <EtsyListingStateSelector listingState={state} setListingState={setState} />
      </Grid>

      <InfiniteScroll
        dataLength={data.length}
        next={() => {
          if (state) {
            firebaseAuth.currentUser?.getIdToken().then((token) => {
              if (!token) {
                showMessage('Token error, please try to refresh the page or login again');
                return;
              }
              fetchListingsByShop(token, selectedAccount.id, {
                state,
                offset: data.length,
                includes: [EtsyParamIncludesEnum.IMAGES],
                limit: 10,
              }).then((response) => {
                setListings((prevListings) => ({
                  ...prevListings,
                  [state]: {
                    ...prevListings[state],
                    results: uniqueObjectWithKey([...prevListings[state].results, ...response.results], 'listing_id'),
                  },
                }));
              });
            });
          }
        }}
        hasMore={!!state && listings[state].count > listings[state].results?.length}
        uniqueKey={state || 'undefined'}
        height={600}
        loader={<CircularProgress />}
      >
        {data.map((listing: EtsyListing) => (
          <EtsyUpdateListingSeoItem
            selectedAccount={selectedAccount}
            user={user}
            showMessage={showMessage}
            key={listing.listing_id}
            listing={listing}
            updatedListing={async (listingId) => {
              if (!state) return;
              const token = await firebaseAuth.currentUser?.getIdToken();
              if (!token) {
                showMessage('Token error, please try to refresh the page or login again');
                return;
              }
              const listingResponse = await fetchListing(token, selectedAccount.id, listingId.toString(), [
                EtsyParamIncludesEnum.IMAGES,
              ]);
              setListings((prevListings) => ({
                ...prevListings,
                [state]: {
                  ...prevListings[state],
                  results: prevListings[state].results.map((l: EtsyListing) =>
                    l.listing_id === listingResponse.listing_id ? listingResponse : l,
                  ),
                },
              }));
              showMessage('Listing updated', undefined, 'success');
            }}
          />
        ))}
      </InfiniteScroll>
    </Grid>
  );
}
